import { createContext, createElement, useContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@/context/AuthContext';
import { apiRequest } from '@/lib/auth';
import type { NoteStatsMap } from '@/lib/exercise/engine';
import {
  makeAttemptId,
  trimAttempts,
  type AttemptRecord,
} from '@/lib/progress/history';

const STORAGE_KEY = 'ler-na-clave:progress:v2';
const LEGACY_KEY = 'ler-na-clave:progress:v1';
const HISTORY_KEY = 'ler-na-clave:attempts:v1';

export interface StageProgress {
  attempts: number;
  correct: number;
  wrong: number;
  bestStreak: number;
  mastered: boolean;
  /**
   * Índice de domínio responsivo (0–100).
   * Diferente da média histórica, ele sobe a cada acerto e cai a cada erro,
   * evitando que centenas de tentativas antigas "congelem" a evolução.
   */
  masteryScore?: number;
}

export interface LessonProgress {
  lessonId: number;
  attempts: number;
  correct: number;
  wrong: number;
  bestStreak: number;
  mastered: boolean;
  noteStats: NoteStatsMap;
  /** posição atual dentro das aulas-carrossel */
  stageIndex: number;
  /** desempenho independente de cada etapa interna */
  stageData: Record<string, StageProgress>;
}

export type ProgressMap = Record<string, LessonProgress>;

export function emptyStageProgress(): StageProgress {
  return {
    attempts: 0,
    correct: 0,
    wrong: 0,
    bestStreak: 0,
    mastered: false,
    masteryScore: 50,
  };
}

export function emptyLessonProgress(lessonId: number): LessonProgress {
  return {
    lessonId,
    attempts: 0,
    correct: 0,
    wrong: 0,
    bestStreak: 0,
    mastered: false,
    noteStats: {},
    stageIndex: 0,
    stageData: {},
  };
}

interface RawLessonProgress {
  lessonId?: number;
  attempts?: number;
  correct?: number;
  wrong?: number;
  bestStreak?: number;
  mastered?: boolean;
  completed?: boolean;
  noteStats?: NoteStatsMap;
  stageIndex?: number;
  stageData?: Record<string, StageProgress>;
}

function normalizeStageProgress(raw: StageProgress): StageProgress {
  const attempts = raw?.attempts ?? 0;
  const correct = raw?.correct ?? 0;
  const legacyAccuracy = attempts > 0 ? Math.round((correct / attempts) * 100) : 50;
  return {
    attempts,
    correct,
    wrong: raw?.wrong ?? 0,
    bestStreak: raw?.bestStreak ?? 0,
    mastered: raw?.mastered ?? false,
    masteryScore:
      typeof raw?.masteryScore === 'number'
        ? Math.max(0, Math.min(100, Math.round(raw.masteryScore)))
        : legacyAccuracy,
  };
}

function normalizeEntry(lessonId: number, raw: RawLessonProgress): LessonProgress {
  const stageData: Record<string, StageProgress> = {};
  if (raw.stageData && typeof raw.stageData === 'object') {
    Object.entries(raw.stageData).forEach(([key, value]) => {
      stageData[key] = normalizeStageProgress(value);
    });
  }

  return {
    lessonId,
    attempts: raw.attempts ?? 0,
    correct: raw.correct ?? 0,
    wrong: raw.wrong ?? 0,
    bestStreak: raw.bestStreak ?? 0,
    mastered: raw.mastered ?? raw.completed ?? false,
    noteStats: raw.noteStats && typeof raw.noteStats === 'object' ? raw.noteStats : {},
    stageIndex: Number.isFinite(raw.stageIndex) ? Math.max(0, raw.stageIndex ?? 0) : 0,
    stageData,
  };
}

function parseMap(raw: string | null): ProgressMap | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, RawLessonProgress>;
    if (!parsed || typeof parsed !== 'object') return null;
    const map: ProgressMap = {};
    Object.entries(parsed).forEach(([key, value]) => {
      const id = Number(key);
      if (!Number.isFinite(id)) return;
      map[key] = normalizeEntry(id, value ?? {});
    });
    return map;
  } catch {
    return null;
  }
}

function loadProgress(): ProgressMap {
  if (typeof window === 'undefined') return {};
  const current = parseMap(window.localStorage.getItem(STORAGE_KEY));
  if (current) return current;
  const legacy = parseMap(window.localStorage.getItem(LEGACY_KEY));
  return legacy ?? {};
}

function loadAttempts(): AttemptRecord[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AttemptRecord[];
    if (!Array.isArray(parsed)) return [];
    return trimAttempts(
      parsed.filter(
        (item) =>
          item &&
          typeof item.id === 'string' &&
          typeof item.lessonId === 'number' &&
          typeof item.at === 'number',
      ),
    );
  } catch {
    return [];
  }
}

function normalizeServerMap(raw: unknown): ProgressMap {
  if (!raw || typeof raw !== 'object') return {};
  const map: ProgressMap = {};
  Object.entries(raw as Record<string, RawLessonProgress>).forEach(([key, value]) => {
    const id = Number(key);
    if (!Number.isFinite(id)) return;
    map[key] = normalizeEntry(id, value ?? {});
  });
  return map;
}

function useProgressState() {
  const { user, loading } = useAuth();
  const [progress, setProgress] = useState<ProgressMap>(() => loadProgress());
  const [attempts, setAttempts] = useState<AttemptRecord[]>(() => loadAttempts());
  const [serverReady, setServerReady] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(attempts));
    } catch {
      // cache local indisponível; o servidor continua sendo a fonte persistente
    }
  }, [progress, attempts]);

  useEffect(() => {
    let cancelled = false;
    if (loading) return undefined;

    if (!user) {
      setProgress({});
      setAttempts([]);
      setServerReady(false);
      return () => {
        cancelled = true;
      };
    }

    setServerReady(false);
    apiRequest<{ progress: unknown; attempts: AttemptRecord[] }>('/api/state')
      .then(async (result) => {
        if (cancelled) return;
        const serverProgress = normalizeServerMap(result.progress);
        const serverAttempts = Array.isArray(result.attempts) ? trimAttempts(result.attempts) : [];
        const serverHasData = Object.keys(serverProgress).length > 0 || serverAttempts.length > 0;

        if (serverHasData) {
          setProgress(serverProgress);
          setAttempts(serverAttempts);
        } else {
          const localProgress = loadProgress();
          const localAttempts = loadAttempts();
          if (Object.keys(localProgress).length > 0 || localAttempts.length > 0) {
            await apiRequest('/api/state', {
              method: 'PUT',
              body: JSON.stringify({ progress: localProgress, attempts: localAttempts }),
            });
            if (cancelled) return;
            setProgress(localProgress);
            setAttempts(localAttempts);
          } else {
            setProgress({});
            setAttempts([]);
          }
        }
        if (!cancelled) setServerReady(true);
      })
      .catch(() => {
        if (!cancelled) setServerReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [loading, user]);

  useEffect(() => {
    if (!user || !serverReady) return undefined;
    const id = window.setTimeout(() => {
      apiRequest('/api/state', {
        method: 'PUT',
        body: JSON.stringify({ progress, attempts }),
      }).catch(() => {
        // fica no cache local e será reenviado numa próxima alteração
      });
    }, 500);
    return () => window.clearTimeout(id);
  }, [attempts, progress, serverReady, user]);

  const getLessonProgress = useCallback(
    (lessonId: number): LessonProgress =>
      progress[String(lessonId)] ?? emptyLessonProgress(lessonId),
    [progress],
  );

  const updateLesson = useCallback(
    (lessonId: number, updater: (previous: LessonProgress) => LessonProgress) => {
      setProgress((current) => {
        const key = String(lessonId);
        const previous = current[key] ?? emptyLessonProgress(lessonId);
        return { ...current, [key]: updater(previous) };
      });
    },
    [],
  );

  const resetAll = useCallback(() => {
    setProgress({});
    setAttempts([]);
  }, []);

  const addAttempt = useCallback(
    (record: Omit<AttemptRecord, 'id' | 'at'>) => {
      const full: AttemptRecord = { ...record, id: makeAttemptId(), at: Date.now() };
      setAttempts((current) => trimAttempts([full, ...current]));
    },
    [],
  );

  const clearAttempts = useCallback(() => setAttempts([]), []);

  const masteredCount = Object.values(progress).filter((item) => item.mastered).length;

  return {
    progress,
    getLessonProgress,
    updateLesson,
    resetAll,
    masteredCount,
    attempts,
    addAttempt,
    clearAttempts,
    serverReady,
  };
}

// A única instância permanece montada enquanto o aluno navega.
// Assim, trocar de aula não cancela a gravação nem restaura um estado antigo.
const ProgressContext = createContext<ReturnType<typeof useProgressState> | null>(null);

export function ProgressProvider({ children }: { children: ReactNode }) {
  const value = useProgressState();
  return createElement(ProgressContext.Provider, { value }, children);
}

export function useProgress() {
  const value = useContext(ProgressContext);
  if (!value) throw new Error('useProgress precisa estar dentro de ProgressProvider');
  return value;
}
